import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useHistory, useLocation } from "react-router-dom";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { GET_COST_DRIVERS, GET_BRANCHES } from "./queries";
import { CostDriverItem } from "./models";
import InventoryNav from "./inventoryNav";

const CostDrivers: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .split("T")[0];
  const today = now.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(
    params.get("startDate") || firstDay
  );
  const [endDate, setEndDate] = useState(params.get("endDate") || today);
  const [branchFilter, setBranchFilter] = useState(
    params.get("branch_id") || ""
  );

  const [fetchDrivers, { data, loading }] = useLazyQuery(GET_COST_DRIVERS, {
    fetchPolicy: "no-cache",
  });
  const [fetchBranches, { data: branchData }] = useLazyQuery(GET_BRANCHES, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    fetchBranches();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchDrivers({
      variables: {
        startDate,
        endDate,
        branch_id: branchFilter ? parseInt(branchFilter) : undefined,
        limit: 30,
      },
    });
    //eslint-disable-next-line
  }, [startDate, endDate, branchFilter]);

  const report = data?.getCostDrivers;
  const items: CostDriverItem[] = report?.items || [];
  const maxCost = items.length > 0 ? items[0].total_cost : 1;

  return (
    <>
      <div className="d-flex align-items-center mb-4">
        <button
          className="btn btn-light mr-3"
          onClick={() => history.push("/inventory/food-cost")}
        >
          <i className="fa fa-arrow-left" />
        </button>
        <h4 className="mb-0 font-weight-bold">
          {lang("Top Costos de Ingredientes", "Top Ingredient Cost Drivers")}
        </h4>
      </div>
      <InventoryNav active="food-cost" />

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-lg-2">
          <label className="small text-muted mb-1">
            {lang("Desde", "From")}
          </label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-lg-2">
          <label className="small text-muted mb-1">
            {lang("Hasta", "To")}
          </label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-lg-2">
          <label className="small text-muted mb-1">
            {lang("Sucursal", "Branch")}
          </label>
          <select
            className="custom-select custom-select-sm"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
          >
            <option value="">{lang("Todas", "All")}</option>
            {branchData?.getBranches?.map((b: any) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <LoaderDots fullSize />}

      {report && (
        <>
          {/* Total cost card */}
          <div className="row mb-3">
            <div className="col-lg-4">
              <div className="material_card p-3 text-center">
                <small className="text-muted">
                  {lang("Costo Total Consumido", "Total Cost Consumed")}
                </small>
                <h3>
                  $
                  {Number(report.total_cost).toLocaleString("es-MX", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </h3>
              </div>
            </div>
          </div>

          {/* Cost drivers table with inline bar chart */}
          <div className="row">
            <div className="col-lg-12">
              <div className="material_card p-3 table-responsive">
                <table className="table vertical_middle">
                  <thead className="thead-light">
                    <tr>
                      <th style={{ width: "5%" }}>#</th>
                      <th style={{ width: "25%" }}>
                        {lang("Ingrediente", "Ingredient")}
                      </th>
                      <th className="text-right" style={{ width: "12%" }}>
                        {lang("Cantidad", "Quantity")}
                      </th>
                      <th className="text-right" style={{ width: "12%" }}>
                        {lang("Costo", "Cost")}
                      </th>
                      <th className="text-right" style={{ width: "8%" }}>
                        {lang("% Total", "% Total")}
                      </th>
                      <th style={{ width: "38%" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => {
                      const barWidth =
                        maxCost > 0
                          ? Math.round((item.total_cost / maxCost) * 100)
                          : 0;

                      return (
                        <tr key={item.ingredient?.id || idx}>
                          <td className="text-muted">{idx + 1}</td>
                          <td className="font-weight-bold">
                            {item.ingredient?.name || "—"}
                            <small className="text-muted ml-1">
                              ({item.ingredient?.unit})
                            </small>
                          </td>
                          <td className="text-right">
                            {Number(item.total_quantity).toLocaleString(
                              "es-MX",
                              { maximumFractionDigits: 2 }
                            )}{" "}
                            {item.ingredient?.unit}
                          </td>
                          <td className="text-right font-weight-bold">
                            $
                            {Number(item.total_cost).toLocaleString("es-MX", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="text-right">
                            {item.pct_of_total.toFixed(1)}%
                          </td>
                          <td>
                            <div
                              style={{
                                height: 16,
                                background: "#e9ecef",
                                borderRadius: 4,
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  width: `${barWidth}%`,
                                  background:
                                    idx < 3
                                      ? "#dc3545"
                                      : idx < 7
                                      ? "#ffc107"
                                      : "#28a745",
                                  borderRadius: 4,
                                  transition: "width 0.3s ease",
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          {lang(
                            "No hay datos de consumo en este período",
                            "No consumption data for this period"
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CostDrivers;
